/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.com/docs/use-static-query/
 */

import { graphql, useStaticQuery } from "gatsby"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"

import thumbnailImage from "../images/thumbnails/og-image.png"

function SEO({
  description,
  lang = "en",
  meta,
  metaImage,
  title,
  pathName,
  keywords: customKeywords,
  isHomePage,
  noIndex = false,
  breadcrumbs,
}) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            name
            title
            description
            fbId
            url
            keywords
          }
        }
        allMarkdownRemark(
          filter: { fileAbsolutePath: { regex: "/reviews/" } }
        ) {
          nodes {
            frontmatter {
              name
            }
            rawMarkdownBody
          }
        }
      }
    `
  )

  const { fbId, url, keywords: defaultKeywords } = site.siteMetadata
  const metaDescription = description || site.siteMetadata.description
  const defaultTitle = description || site.siteMetadata?.title
  const thumbnailImageUrl = metaImage
    ? `${url}${metaImage}`
    : `${url}${thumbnailImage}`

  const dynamicKeywords = isHomePage ? defaultKeywords : customKeywords
  const pageTitle = title || defaultTitle

  // H5: WebSite schema only on homepage; WebPage on inner pages
  const schemaOrgJSONLD = isHomePage
    ? {
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: url,
        name: site.siteMetadata.name,
        description: site.siteMetadata.description,
        publisher: {
          "@type": "Organization",
          name: site.siteMetadata.name,
          logo: {
            "@type": "ImageObject",
            url: `${url}${thumbnailImage}`,
          },
        },
      }
    : {
        "@context": "https://schema.org",
        "@type": "WebPage",
        url: `${url}${pathName || ""}`,
        name: title,
        description: metaDescription,
        isPartOf: {
          "@type": "WebSite",
          name: site.siteMetadata.name,
          url: url,
        },
      }

  // M15: Organization schema with description, contactPoint, foundingLocation
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Premagic",
    url: "https://premagic.com",
    logo: `${url}${thumbnailImage}`,
    description:
      "AI-powered event technology platform that transforms event attendees into passionate brand advocates through intelligent photo sharing, AI avatars, and smart networking.",
    foundingDate: "2018",
    foundingLocation: {
      "@type": "Place",
      name: "Bengaluru, India",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "sales",
      url: "https://premagic.com/request-a-demo/",
      email: "sales@premagic.com",
    },
    sameAs: [
      "https://www.linkedin.com/company/premagic/",
      "https://www.facebook.com/premagicdotcom/",
      "https://www.instagram.com/premagicdotcom/",
      "https://www.youtube.com/channel/UCyhi-GvR0rrBq639Z8uWtqw",
      "https://www.g2.com/products/premagic/reviews",
      "https://www.crunchbase.com/organization/premagic",
      "https://www.capterra.com/p/236853/Premagic/",
    ],
  }

  // H6: SoftwareApplication with price, priceCurrency, aggregateRating
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Premagic",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "AI-powered event technology platform that turns attendees into brand advocates through intelligent photo sharing, AI avatars, and smart networking.",
    url: "https://premagic.com",
    offers: {
      "@type": "Offer",
      url: "https://premagic.com/pricing/us/",
      price: "0",
      priceCurrency: "USD",
      description: "Usage-based pricing. Contact for custom quote.",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "150",
      bestRating: "5",
    },
    award:
      "Event Tech Awards 2025 - Best Use of Technology for Marketing an Event",
  }

  const breadcrumbSchema = breadcrumbs
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1
          const listItem = {
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
          }
          if (!isLast) {
            listItem["item"] = `https://premagic.com${item.url}`
          }
          return listItem
        }),
      }
    : null

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={pageTitle}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          name: "keywords",
          content: dynamicKeywords?.join(","),
        },
        ...(noIndex
          ? [
              {
                name: "robots",
                content: "noindex, nofollow",
              },
            ]
          : []),
        // facebook
        {
          property: `og:title`,
          content: pageTitle,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: "og:image",
          content: thumbnailImageUrl,
        },
        {
          property: "og:url",
          content: `${url}${pathName || ""}`,
        },
        {
          property: "og:site_name",
          content: "Premagic",
        },
        {
          name: `twitter:card`,
          content: `summary_large_image`,
        },
        {
          name: `twitter:title`,
          content: pageTitle,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: "twitter:image",
          content: thumbnailImageUrl,
        },
        {
          name: "fb:app_id",
          content: fbId,
        },
      ].concat(meta)}
    >
      {/* Schema.org tags */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link
        href="https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&family=Merriweather:ital,opsz,wght@0,18..144,300..900;1,18..144,300..900&display=swap"
        rel="stylesheet"
        media="print"
        onLoad="this.media='all'"
      />
      <noscript>{`<link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700&display=swap" rel="stylesheet" />`}</noscript>
      <link rel="canonical" href={`https://premagic.com${pathName}`} />
      {pathName === "/pricing/us/" && (
        <link
          rel="alternate"
          hrefLang="en-us"
          href="https://premagic.com/pricing/us/"
        />
      )}
      {pathName === "/pricing/us/" && (
        <link
          rel="alternate"
          hrefLang="en-in"
          href="https://premagic.com/pricing/in/"
        />
      )}
      {pathName === "/pricing/in/" && (
        <link
          rel="alternate"
          hrefLang="en-in"
          href="https://premagic.com/pricing/in/"
        />
      )}
      {pathName === "/pricing/in/" && (
        <link
          rel="alternate"
          hrefLang="en-us"
          href="https://premagic.com/pricing/us/"
        />
      )}
      {(pathName === "/pricing/us/" || pathName === "/pricing/in/") && (
        <link
          rel="alternate"
          hrefLang="x-default"
          href="https://premagic.com/pricing/us/"
        />
      )}
      <script
        type="text/javascript"
        src="https://crm.zoho.in/crm/javascript/zcga.js"
        async
      ></script>
      <script type="application/ld+json">
        {JSON.stringify(schemaOrgJSONLD)}
      </script>
      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}
      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify(softwareSchema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  metaImage: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string.isRequired,
}

export default SEO
